/**
 * 🏠 **The búr — the storehouse, and where it stands.**
 *
 * **D109, T135.** 🎯 **haugur, 2026-09-03:** *"its not a chest or a block, its a
 * building that gets placed where you point it… like a chest but bigger and
 * like a hous"* — and, shown it as cubes: *"it does not need to be blocky."*
 *
 * ## 🔴 One building, two blocks, and only one of them is ever drawn
 *
 * | | |
 * |---|---|
 * | **`bur`** | The DOOR. One cell, with a `facing` state like the torch's. It is the anchor everything else is worked out from, and later the container you click |
 * | **`burhluti`** | *"Part of a storehouse."* Every other cell of the footprint. Solid — so walls stop you and the roof shelters you — and it refuses a hit with *take it down at the door* |
 *
 * ⚠️ **Neither is drawn by the mesher.** Both are on `drawnOutsideTheMesh`, as
 * the torch and the driftwood are, and the client draws **one 3D model** over
 * the whole footprint (`client/src/burmesh.ts`) — picture 2 turned into a mesh.
 *
 * ## 🔴 The footprint is DERIVED, never stored
 *
 * Given the door's cell and which way it faces, every cell of the building
 * follows. So there is no table of storehouses, no migration, and nothing that
 * can disagree with the world: **the door IS the record.** `Minecraft/02` §4.1
 * calls this split — an authored orientation, a derived shape — the cleanest
 * idea in Minecraft's block model.
 *
 * ## 📏 5 wide, 9 long, 5 high
 *
 * The first one was 12 long — the photograph's own 1 : 2.5, scaled uniformly
 * to 5 m wide. 🎯 haugur, having walked round it: *"2 to 3 blocks shorter."*
 * So the model is scaled to 5 m across and **9 m along**, and the height keeps
 * the width's scale (4.9 m): only the ridge is shortened, and the gable is
 * still the photograph's shape.
 *
 * ## The frame
 *
 * `facing` is the way the DOOR faces — towards the player who placed it. The
 * building extends the other way (*into*), and *right* is the player's right
 * as they look at the door. The door cell is the front row's centre.
 */

import { blockIdForKey } from './blocks.ts';
import {
  BUR_RULES,
  CHEST_BLOCK,
  CHEST_RULES,
  LOOM_RULES,
  SKEMMA_RULES,
  VEFSTOFA_RULES,
  type ContainerRules,
} from './chest.ts';
import { baseBlockOf, stateOf, withState } from './states.ts';

export const BUR_BLOCK = 'bur';
export const SKEMMA_BLOCK = 'skemma';
export const SMIDJA_BLOCK = 'smidja';
/** 🧵 The textile room — D127. */
export const VEFSTOFA_BLOCK = 'vefstofa';
export const BUR_PART = 'burhluti';
/** The item. Placing it stamps the whole building. */
export const BUR_ITEM = 'bur';

/**
 * 🏠 **The pit-house vefstofa's five blocks — D135, T289.** Named here rather
 * than in `pithouse.ts`, which imports this file: `containerRulesOf` below
 * needs the bench, and the other way round would be two files importing each
 * other — the load-order fault `slaughter.ts` records.
 */
export const PIT_DOOR = 'vefstofudyr';
/** 🧱 Walls and ceiling: solid, unbreakable, taken down at the door. */
export const PIT_PART = 'vefstofuhluti';
/** 🪜 The half-height stone step inside the door. */
export const PIT_STEP = 'vefstofuthrep';
/** 🔥 The stone-slab oven in the back-right corner. */
export const PIT_OVEN = 'vefstofuofn';
/** 🧵 The bench along the left wall — where wool is spun. */
export const PIT_BENCH = 'vefstofubekkur';
/** 🟫 The beaten-earth floor laid under the room — the ground there would otherwise be a lawn. */
export const PIT_FLOOR = 'vefstofugolf';

/**
 * 🏘️ **The three turf houses — D110, 2026-09-04.**
 *
 * 🎯 **haugur:** *"i want to use the same mechanism as búr, use the same
 * graphic, but put a sign above the door… When players have built all 3 and
 * place the side by side they should get the old burstabær feeling."*
 *
 * 🔴 **They are ONE BUILDING with three doors.** One model, one part-block
 * (`burhluti`), one footprint derivation, one recipe cost, one takedown. **The
 * carved board above the door is the only thing that tells them apart.**
 *
 * | | |
 * |---|---|
 * | `bur` | 🥫 54 slots, **food only** |
 * | `skemma` | 📦 54 slots, **everything except food** — the búr's rule reversed |
 * | `smidja` | 🔨 **no slots at all**; its door opens the craft list |
 *
 * ⚠️ **This table is the ONLY list of them.** Everything else asks it —
 * {@link isBur}, {@link containerRulesOf}, the mesher, the server's stamp. **A
 * second list beside it is the fault this repo names oftener than any other**,
 * and a fourth house must therefore be a row here and nothing else.
 */
export const HOUSES = [BUR_BLOCK, SKEMMA_BLOCK, SMIDJA_BLOCK, VEFSTOFA_BLOCK] as const;
export type HouseKind = (typeof HOUSES)[number];

/**
 * 🔨 **The workshop borrows the bench's craft list**, rather than owning
 * recipes of its own.
 *
 * 🎯 **haugur:** *"same mechanics as smíðabekkur."* 🔴 **So a recipe never
 * mentions the smiðja** — `STATION_KEYS` is derived from the recipes, and a
 * second station name on every row would be two lists to keep in step. **One
 * alias, asked by the client when it opens a screen and by the server when it
 * checks a craft**, is the whole of it.
 */
export const STATION_ALIAS: ReadonlyMap<string, string> = new Map([[SMIDJA_BLOCK, 'smidabekkur']]);

/** Which station a block really is, for crafting. Itself, unless it borrows one. */
export function stationFor(blockKey: string): string {
  return STATION_ALIAS.get(blockKey) ?? blockKey;
}

export const BUR_FACINGS = ['north', 'east', 'south', 'west'] as const;
export type BurFacing = (typeof BUR_FACINGS)[number];

/**
 * Cells across (right), along (into), and up.
 *
 * 🎯 **haugur, 2026-09-03, having placed the first one:** *"i think it could
 * be 2 to 3 blocks shorter."* **12 became 9**, and only the LENGTH: the model
 * is stretched along its ridge and the gable keeps the photograph's shape.
 */
export const BUR_FOOTPRINT = { width: 5, length: 9, height: 5 } as const;

/**
 * 🔴 **The longest a storehouse has EVER been — for taking one down.**
 *
 * The first slice shipped 12 long and haugur placed one before it became 9.
 * A takedown that cleared only 9 rows would leave three rows of invisible,
 * solid `burhluti` standing behind the building for ever. So the takedown
 * sweeps the LONGEST footprint any build has written, and clears only cells
 * that are part-blocks — nothing else in that space is touched. ⚠️ **If the
 * length ever grows past this, raise this too.**
 */
export const BUR_LONGEST_LENGTH = 12;

/** 🥫 What the door holds — D109: twice a chest, and nothing but food. */
export const BUR_SLOTS = BUR_RULES.slots;

/**
 * 🔑 **Which container rules a block answers to**, or `undefined` for a block
 * that is not a container. One place, asked by the server for every open and
 * every move — so the chest and the búr cannot come to disagree about who
 * holds what.
 */
export function containerRulesOf(id: number): ContainerRules | undefined {
  /* 🧵 D135 — the pit house's bench spins, with the vefstofa's own two slots. */
  if (isPitBenchId(id)) return VEFSTOFA_RULES;
  /* 🧵 D136 — the loom's anchor, in any state, weaves. */
  if (isLoomId(id)) return LOOM_RULES;
  const kind = houseKindOf(id);
  if (kind === BUR_BLOCK) return BUR_RULES;
  if (kind === SKEMMA_BLOCK) return SKEMMA_RULES;
  if (kind === VEFSTOFA_BLOCK) return VEFSTOFA_RULES;
  /* 🔨 The smiðja is NOT a container. Its door opens the craft list, and
     returning rules here would silently give it 54 slots nobody asked for. */
  if (kind === SMIDJA_BLOCK) return undefined;
  const base = blockIdForKey(CHEST_BLOCK);
  if (base !== undefined && id === base) return CHEST_RULES;
  return undefined;
}

/** The block key a container's contents are filed under in `block_data`. */
export function containerBlockKeyOf(id: number): string | undefined {
  /* 🧵 D135 — filed under the bench's own key, apart from any old turf vefstofa. */
  if (isPitBenchId(id)) return PIT_BENCH;
  if (isLoomId(id)) return LOOM_BLOCK_KEY;
  const kind = houseKindOf(id);
  /* 🔴 The kind's OWN key, so a búr's food and a skemma's goods are filed
     apart. Returning `bur` for both would have poured one into the other. */
  if (kind !== undefined) return kind === SMIDJA_BLOCK ? undefined : kind;
  const base = blockIdForKey(CHEST_BLOCK);
  return base !== undefined && id === base ? CHEST_BLOCK : undefined;
}

interface Cell {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** Which way each facing points, on the ground. North is −z, as the torch has it. */
const VECTOR: ReadonlyMap<BurFacing, { readonly x: number; readonly z: number }> = new Map([
  ['north', { x: 0, z: -1 }],
  ['east', { x: 1, z: 0 }],
  ['south', { x: 0, z: 1 }],
  ['west', { x: -1, z: 0 }],
]);

/**
 * 🏘️ **Which of the three houses this block's door is**, or `undefined` for
 * anything that is not a door — D110.
 */
export function houseKindOf(id: number): HouseKind | undefined {
  const base = baseBlockOf(id);
  for (const kind of HOUSES) if (blockIdForKey(kind) === base) return kind;
  return undefined;
}

/**
 * Is this a turf-house door — any of the three?
 *
 * ⚠️ **The name is the búr's because the búr was first**, and it is asked in
 * about twenty places that all mean *a house door*. **D110 widened what it
 * answers, not what it is called.**
 */
export function isBur(id: number): boolean {
  return houseKindOf(id) !== undefined;
}

/**
 * 🔥 **Does this building have a FIRE in it?** — D121, and #737 is why it
 * exists.
 *
 * 🎯 **haugur, 2026-09-23:** *"there should not be a fire in this menu"* — with
 * a picture of a **Skemma** showing **Food · Fire** tabs.
 *
 * 🔴 **`isBur` DOES NOT MEAN "IS A BÚR". It means "is any of the three
 * houses"** — búr, skemma and smiðja all answer true, because every house door
 * is one block with a `facing`. ⚠️ **So guarding a búr-only feature with it
 * gave the skemma a cooking fire**, and would have given the smiðja one too.
 *
 * 🔴 **THAT IS `06_Traps.md` RULE 3, AND THE EXAMPLE IN THE RULE IS THIS SAME
 * BUILDING:** *a name derived from a proxy is wrong the day a second thing
 * shares it — a skemma was called* **Búr** *because 54 slots meant búr.*
 * 👉 ***This time a skemma got a FIRE because a function called `isBur` said
 * yes.***
 *
 * ✅ **So the question is asked by NAME rather than by proxy**, and the answer
 * is a property of the building. **The day a fourth house has a fire, it is one
 * row here.**
 */
/** 🧵 **Does this house SPIN?** Asked by name, for `houseHasFire`'s reason — D127. */
export function houseSpins(id: number): boolean {
  return houseKindOf(id) === VEFSTOFA_BLOCK || isPitBenchId(id);
}

/** 🧵 The loom's key — named here as well as in `loom.ts`, which imports this file. */
const LOOM_BLOCK_KEY = 'vefstadur';

/** 🧵 A loom's anchor in any state — asked by key, for the bench's reason below. */
function isLoomId(id: number): boolean {
  const base = blockIdForKey(LOOM_BLOCK_KEY);
  return base !== undefined && baseBlockOf(id) === base;
}

/** 🧵 The pit house's bench — asked here, by key, because `pithouse.ts` imports this file. */
function isPitBenchId(id: number): boolean {
  const bench = blockIdForKey(PIT_BENCH);
  return bench !== undefined && id === bench;
}

export function houseHasFire(id: number): boolean {
  return houseKindOf(id) === BUR_BLOCK;
}

export function isBurPart(id: number): boolean {
  const base = blockIdForKey(BUR_PART);
  return base !== undefined && id === base;
}

export function burPartId(): number | undefined {
  return blockIdForKey(BUR_PART);
}

/** Which way a door faces, or `undefined` for anything that is not a door. */
export function burFacing(id: number): BurFacing | undefined {
  if (!isBur(id)) return undefined;
  /* Every house carries the same `facing` property, so one reader serves all
     three — the states file gives them identical definitions on purpose. */
  const facing = stateOf(id)['facing'];
  return typeof facing === 'string' && (BUR_FACINGS as readonly string[]).includes(facing)
    ? (facing as BurFacing)
    : 'north';
}

export function burIdFor(facing: BurFacing, kind: HouseKind = BUR_BLOCK): number | undefined {
  const base = blockIdForKey(kind);
  if (base === undefined) return undefined;
  return withState(base, { facing });
}

/**
 * 🧭 Which way the door should face so that it looks at the player.
 *
 * The client's forward is `(−sin yaw, −cos yaw)` (`movement.ts`), so the door
 * faces back along it: `(sin yaw, cos yaw)`, snapped to the nearer axis. **The
 * building then extends away from the player**, which is what pointing at a
 * plot and placing a house means.
 */
export function burFacingToward(yaw: number): BurFacing {
  const x = Math.sin(yaw);
  const z = Math.cos(yaw);
  if (Math.abs(x) > Math.abs(z)) return x > 0 ? 'east' : 'west';
  return z > 0 ? 'south' : 'north';
}

/** The three directions of the building's own frame, from its facing. */
export function burFrame(facing: BurFacing): {
  readonly into: { readonly x: number; readonly z: number };
  readonly right: { readonly x: number; readonly z: number };
} {
  const f = VECTOR.get(facing) ?? { x: 0, z: -1 };
  /* `|| 0` turns a −0 into 0: deep equality treats them as different, and a
     −0 in a coordinate is a thing that prints as a fault while being none. */
  return { into: { x: -f.x || 0, z: -f.z || 0 }, right: { x: -f.z || 0, z: f.x || 0 } };
}

function cellAt(door: Cell, facing: BurFacing, d: number, w: number, h: number): Cell {
  const { into, right } = burFrame(facing);
  return {
    x: door.x + into.x * d + right.x * w,
    y: door.y + h,
    z: door.z + into.z * d + right.z * w,
  };
}

/**
 * 🏠 **Every cell of the building except the door.**
 *
 * Row 0 is the door's row; width runs from −2 to +2 with the door at 0. The
 * building is SOLID all the way through — haugur chose *"solid"* over a room you
 * walk into, and a solid object is one nobody can get stuck inside of.
 */
export function burFootprint(door: Cell, facing: BurFacing): Cell[] {
  const cells: Cell[] = [];
  const half = (BUR_FOOTPRINT.width - 1) / 2;
  for (let d = 0; d < BUR_FOOTPRINT.length; d++)
    for (let w = -half; w <= half; w++)
      for (let h = 0; h < BUR_FOOTPRINT.height; h++) {
        if (d === 0 && w === 0 && h === 0) continue;
        cells.push(cellAt(door, facing, d, w, h));
      }
  return cells;
}

/** The door a cell would belong to, if it sat at `(d, w, h)` of a building facing `facing`. */
function doorFromCell(cell: Cell, facing: BurFacing, d: number, w: number, h: number): Cell {
  const { into, right } = burFrame(facing);
  return {
    x: cell.x - into.x * d - right.x * w,
    y: cell.y - h,
    z: cell.z - into.z * d - right.z * w,
  };
}

/**
 * 🚪 **Which door answers for this cell** — the whole building, not just the
 * doorway.
 *
 * 🎯 **haugur, 2026-09-04:** *"lets fix that i can look anywhere on Búr and
 * press o for the menu, now i need to look at the very bottom of the door for
 * o to work."* A building 5 × 9 × 5 has 224 part-cells and exactly one of its
 * 225 was a menu, so the menu was effectively hidden.
 *
 * 🔴 **The footprint is derived from the door, so this is that derivation run
 * BACKWARDS**, and it must stay in step with {@link burFootprint} — which is
 * why both live in this file and are tested together. For every position a
 * part could occupy, it works out where the door would then be and asks the
 * world whether a door facing that way is really there.
 *
 * ⚠️ **It sweeps {@link BUR_LONGEST_LENGTH}, not the current length**, for the
 * same reason {@link burSweep} does: haugur placed 12-long buildings before the
 * length became 9, and they are still standing.
 *
 * ✅ **The answer cannot be ambiguous.** A match means the cell lies inside
 * that door's footprint, and two footprints containing one cell would mean two
 * buildings overlapping — which {@link plotVerdict} refuses at placing time.
 *
 * @returns the door's cell, or `undefined` for anything that is not part of a
 *   storehouse. A door resolves to itself.
 */
export function burDoorFor(
  cell: Cell,
  blockAt: (x: number, y: number, z: number) => number,
): Cell | undefined {
  const here = blockAt(cell.x, cell.y, cell.z);
  if (isBur(here)) return { x: cell.x, y: cell.y, z: cell.z };
  if (!isBurPart(here)) return undefined;

  const half = (BUR_FOOTPRINT.width - 1) / 2;
  for (let h = 0; h < BUR_FOOTPRINT.height; h++)
    for (let d = 0; d < BUR_LONGEST_LENGTH; d++)
      for (let w = -half; w <= half; w++)
        for (const facing of BUR_FACINGS) {
          const door = doorFromCell(cell, facing, d, w, h);
          const id = blockAt(door.x, door.y, door.z);
          if (isBur(id) && burFacing(id) === facing) return door;
        }
  return undefined;
}

/**
 * 🧹 **Every cell a storehouse of any length ever built could occupy**, for
 * taking one down — see {@link BUR_LONGEST_LENGTH}. The server clears only
 * the cells in this that actually hold a part-block.
 */
export function burSweep(door: Cell, facing: BurFacing): Cell[] {
  const cells: Cell[] = [];
  const half = (BUR_FOOTPRINT.width - 1) / 2;
  for (let d = 0; d < BUR_LONGEST_LENGTH; d++)
    for (let w = -half; w <= half; w++)
      for (let h = 0; h < BUR_FOOTPRINT.height; h++) {
        if (d === 0 && w === 0 && h === 0) continue;
        cells.push(cellAt(door, facing, d, w, h));
      }
  return cells;
}

/**
 * 🏘️ **How far a house will jump to sit flush with its neighbour**, in metres.
 *
 * ⚠️ **Four is a judgement and it is the only number here.** Too small and the
 * snap never fires when you want it; too large and a house you meant to put on
 * its own leaps sideways into the farm. **Half the building's width** is the
 * defensible reading: aim closer to the neighbour than to the middle of your own
 * plot and you meant to join it on.
 */
export const SNAP_REACH = 4;

/**
 * How far to look for a neighbour, in metres. A flush door sits exactly
 * {@link BUR_FOOTPRINT}`.width` away, so the reach plus the width plus one is
 * everything that could possibly qualify.
 */
const SNAP_SEARCH = BUR_FOOTPRINT.width + SNAP_REACH + 1;

/**
 * 🏘️ **Sit this house flush against a neighbour — D110, 2026-09-04.**
 *
 * 🎯 **haugur, having placed three by hand and seen the gaps:** *"Yes i think
 * they should snap together, dont you think, it looks more authentic."*
 *
 * 🔴 **A burstabær is gables in a ROW, touching.** One block of gap, or one
 * house set forward of the others, and it reads as three sheds rather than a
 * farm — which is exactly what his own screenshot showed.
 *
 * ## What flush means
 *
 * A neighbour's door at `D` facing `g` has **exactly two** flush slots: one
 * building's width to its left and one to its right, along the same front row,
 * **facing the same way**. There is no third answer and nothing to round.
 *
 * ⚠️ **The facing is taken from the NEIGHBOUR, not from where the player is
 * looking.** A row whose gables face different ways is not a row, and this is
 * the whole reason snapping is worth having: it fixes the alignment a person
 * cannot do by eye.
 *
 * ⚠️ **It does NOT check the plot.** A snapped slot that is blocked shows the
 * red shadow like any other — **telling the truth about where it would go beats
 * quietly declining to go there.**
 *
 * @returns where the house should actually stand, or `undefined` to leave the
 *   player's own aim alone
 */
export function snapToNeighbour(
  want: Cell,
  blockAt: (x: number, y: number, z: number) => number,
): { readonly cell: Cell; readonly facing: BurFacing } | undefined {
  let best: { cell: Cell; facing: BurFacing; away: number } | undefined;

  /* 🔴 The neighbourhood is searched at the door's OWN height only. Two houses
     on different levels are not a burstabær, and scanning the levels as well
     would cost five times as much on every frame the shadow is drawn. */
  for (let dx = -SNAP_SEARCH; dx <= SNAP_SEARCH; dx++) {
    for (let dz = -SNAP_SEARCH; dz <= SNAP_SEARCH; dz++) {
      const id = blockAt(want.x + dx, want.y, want.z + dz);
      if (!isBur(id)) continue;
      const facing = burFacing(id);
      if (facing === undefined) continue;
      const door = { x: want.x + dx, y: want.y, z: want.z + dz };
      const { right } = burFrame(facing);
      for (const side of [1, -1]) {
        const cell = {
          x: door.x + right.x * BUR_FOOTPRINT.width * side,
          y: door.y,
          z: door.z + right.z * BUR_FOOTPRINT.width * side,
        };
        const away = Math.hypot(cell.x - want.x, cell.z - want.z);
        if (away > SNAP_REACH) continue;
        if (best === undefined || away < best.away) best = { cell, facing, away };
      }
    }
  }
  return best === undefined ? undefined : { cell: best.cell, facing: best.facing };
}

/**
 * 👻 **Is this plot free and flat?** The two tests the server makes before it
 * stamps a building, as one pure function, so the client's shadow and the
 * server's refusal cannot disagree.
 *
 * @param blockAt a synchronous block lookup — the client's own, or a server
 *   that has pre-awaited its chunks
 * @returns `free`, `blocked` (something in the way) or `unlevel` (a column
 *   with nothing solid under it)
 */
export function plotVerdict(
  door: Cell,
  facing: BurFacing,
  blockAt: (x: number, y: number, z: number) => number,
  isSolid: (id: number) => boolean,
): 'free' | 'blocked' | 'unlevel' {
  if (blockAt(door.x, door.y, door.z) !== 0) return 'blocked';
  for (const c of burFootprint(door, facing)) if (blockAt(c.x, c.y, c.z) !== 0) return 'blocked';
  for (const c of burGround(door, facing)) {
    const g = blockAt(c.x, c.y, c.z);
    if (g === 0 || !isSolid(g)) return 'unlevel';
  }
  return 'free';
}

/** 🟫 The cells the building stands ON — one under every column. */
export function burGround(door: Cell, facing: BurFacing): Cell[] {
  const cells: Cell[] = [];
  const half = (BUR_FOOTPRINT.width - 1) / 2;
  for (let d = 0; d < BUR_FOOTPRINT.length; d++)
    for (let w = -half; w <= half; w++) cells.push(cellAt(door, facing, d, w, -1));
  return cells;
}

/**
 * 📍 Where the model's centre goes, in the same units as the door cell — the
 * middle of the footprint, on the floor. Add 0.5 to x and z for the cell's
 * centre; the caller does that because chunk-local and world cells differ only
 * by an origin.
 */
export function burCentre(door: Cell, facing: BurFacing): Cell {
  const { into } = burFrame(facing);
  const mid = (BUR_FOOTPRINT.length - 1) / 2;
  return { x: door.x + into.x * mid, y: door.y, z: door.z + into.z * mid };
}

/**
 * 📦 **Which rules a stored container row answers to, by the block key it is
 * filed under** — for the bench's search of nearby stores (D111). One list,
 * here beside `containerBlockKeyOf`, so a key that can be WRITTEN is a key that
 * is READ. 🔴 D135's bench was missing from the server's own copy, so its thread
 * did not count toward a craft.
 */
const RULES_BY_STORED_KEY: ReadonlyMap<string, ContainerRules> = new Map([
  [CHEST_BLOCK, CHEST_RULES],
  [BUR_BLOCK, BUR_RULES],
  [SKEMMA_BLOCK, SKEMMA_RULES],
  [VEFSTOFA_BLOCK, VEFSTOFA_RULES],
  [PIT_BENCH, VEFSTOFA_RULES],
]);

/** The rules for a row filed under `key`, or `undefined` for a key no container uses. */
export function rulesForStoredKey(key: string): ContainerRules | undefined {
  return RULES_BY_STORED_KEY.get(key);
}

/** Every block key a container row may be filed under. */
export function storedContainerKeys(): readonly string[] {
  return [...RULES_BY_STORED_KEY.keys()];
}

/**
 * 🧵 **The key a spin catch-up writes its row under**: the one the row already
 * has. ⚠️ It wrote `vefstofa` unconditionally, so a bench's row changed name
 * whenever it spun — and which store it counted as depended on the last write.
 */
export function spinRowKey(row: { readonly blockKey: string } | undefined): string {
  return row?.blockKey ?? VEFSTOFA_BLOCK;
}
